import {
  CreateControllerWSRequest,
  CreateGlobalWSRequest,
  BinaryToUintArray,
  Interval
} from '../Utils/utils.js';
import {
  websocketDebug,
  websocketMessageSummary
} from '../Utils/websocket_logging.js';

import globalOnMessage from './globalOnMessageHook.js';
import { prepareControllerResync } from '../Utils/controller_resync';

export function createWebsocketManager(wasm) {
  return {
    socket: null,
    status: 'off',
    wasm: wasm,
    format: 'json', // or 'msgpack'
    intentionallyClosed: false,
    lastMessageAt: 0,
    watchdogTimeoutMs: 10000,
    // Firefox can defer admission for 60s after failures; allow the existing
    // 10s connection window after that browser-managed delay.
    connectionTimeoutMs: 70000,
    initializationTimeoutMs: 3000,
    noConnectionGraceMs: 20000,
    initializeTimeout: null,
    reconnectTimer: null,
    noConnectionTimer: null,
    reconnectAttempt: 0,
    reconnectDelaysMs: [1000, 2000, 5000, 10000, 30000],
    suspended: false,

    init(store = null) {
      if (store) this.store = store;
      if (!this.store || this.suspended) return false;
      if (document.visibilityState === 'hidden') {
        this.suspend('hidden');
        return false;
      }
      this.address = this.store.address;
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      for (const controller of this.store.controllers ?? []) prepareControllerResync(controller);

      if (
        this.socket &&
        (this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING)
      ) {
        try {
          this.intentionallyClosed = true;
          this.socket.close(1000, 're-init');
          console.log('[wss-conn] createWebsocketManager: Already open socket closed.');
        } catch {
          console.log('[wss-conn] createWebsocketManager: Failed closing already open socket.');
        }
      }

      this.intentionallyClosed = false;
      console.log('[wss-conn] createWebsocketManager: connecting to: ', this.address);
      const t0 = performance.now();
      this.socket = new WebSocket(this.address);
      this.scheduleNoConnectionNotice();
      const socket = this.socket;
      this.lastMessageAt = Date.now();
      clearTimeout(this.initializeTimeout);
      this.initializeTimeout = setTimeout(() => {
        if (this.socket === socket && this.status === 'off') {
          console.log('[wsManager] connection timed out');
          this.fail();
        }
      }, this.connectionTimeoutMs);

      this.socket.binaryType = 'arrayBuffer';
      console.log('[wss-conn] createWebsocketManager: waiting...');

      this.socket.onopen = () => {
        if (this.socket !== socket) return;
        this.lastMessageAt = Date.now();
        console.log(
          '[ws] onopen at',
          new Date().toISOString(),
          'delta_ms=',
          Math.round(performance.now() - t0)
        );
        this.store.hooks.validateSession?.();
        this.sendWSMsg(null, 'initialize_user');

        // in case of failed initialization the server does not respond at all.
        // catch this case here
        clearTimeout(this.initializeTimeout);
        this.initializeTimeout = setTimeout(() => {
          if (this.socket === socket && this.status === 'off') {
            console.log('[wsManager] failed init');
            this.fail();
          }
        }, this.initializationTimeoutMs);
      };

      this.socket.onclose = (event) => {
        if (this.socket !== socket) return;
        console.log('[wss-conn] createWebsocketManager: on close', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });

        if (!this.intentionallyClosed) {
          console.log('[wsManager] socket closed unexpected');
          this.fail();
        }
      };

      this.socket.onerror = () => {
        if (this.socket !== socket) return;
        console.log('[wsManager] socket error');
        this.fail();
      };

      this.socket.onmessage = (event) => {
        if (this.socket !== socket || this.intentionallyClosed || this.suspended) return;
        let data;
        this.lastMessageAt = Date.now();
        if (this.format === 'msgpack') {
          try {
            const reader = new FileReader();
            reader.onload = () => {
              const binary_str = reader.result;
              const len = binary_str.length;
              const new_data = wasm.fromBinary(BinaryToUintArray(binary_str, len), len);

              data = JSON.parse(new_data);
            };
            reader.readAsBinaryString(event.data);
          } catch (e) {
            console.log('[global] Failed parsing json: ', event.data, e);
          }
        } else {
          data = JSON.parse(event.data);
        }

        websocketDebug(
          '[wsManager] received',
          websocketMessageSummary(data, event.data?.byteLength ?? event.data?.length)
        );
        globalOnMessage(this, data);
      };

      return true;
    },

    // setup request loops
    start: function () {
      if (this.controllers_active) this.controllers_active.stop();
      if (this.request_values) this.request_values.stop();
      if (this.connection_watchdog) this.connection_watchdog.stop();

      this.controllers_active = new Interval(
        () => this.sendWSMsg(null, 'controllers_active'),
        2500
      );
      this.controllers_active.run();

      this.request_values = new Interval(() => {
        const controllers = this.store.controllers;
        for (let controller of controllers) {
          if (!controller.connection.request_values_full) {
            this.sendWSMsg(controller, 'request_values_full');
          } else {
            this.sendWSMsg(controller, 'request_values');
          }
        }
      }, 1000);

      this.request_values.run();

      this.connection_watchdog = new Interval(() => {
        if (
          this.status === 'on' &&
          Date.now() - this.lastMessageAt > this.watchdogTimeoutMs
        ) {
          console.log('[wsManager] no messages received, treating socket as failed');
          this.fail();
        }
      }, 5000);

      this.connection_watchdog.run();
    },

    sendWSMsg: function (
      controller,
      command,
      data = {},
      forward = false,
      zoned = false,
      force = false
    ) {
      if (this.socket.readyState !== 1 || (!force && controller?.connection?.status === 'bad'))
        return false;

      const req =
        controller != null
          ? CreateControllerWSRequest(this.socket, controller.id, command, data)
          : CreateGlobalWSRequest(this.socket, command, data);

      // Add cookie
      req.cookie = document.cookie;

      if (forward) {
        req.forward = true;
      }
      req['is_zoned'] = zoned; // read zoned/non-zoned cmds as zoned
      if (zoned && 'zone' in req.data) {
        req.data['id'] = req.data.zone;
      }

      let msg = JSON.stringify(req);

      if (req) {
        if (this.format === 'msgpack' && this.wasm.toBinary) {
          const binary_str = this.wasm.toBinary(msg);
          let binaryData = new Uint8Array(
            atob(binary_str)
              .split('')
              .map(function (c) {
                return c.charCodeAt(0);
              })
          );
          msg = binaryData;
        }

        this.socket.send(msg);
        websocketDebug(
          '[wsManager] sent',
          websocketMessageSummary(req, msg.byteLength ?? msg.length)
        );
        return true;
      }
      return false;
    },

    stop: function (reason, clearReconnect = true, clearNoConnection = true) {
      if (this.controllers_active) this.controllers_active.stop();
      if (this.request_values) this.request_values.stop();
      if (this.connection_watchdog) this.connection_watchdog.stop();
      clearTimeout(this.initializeTimeout);
      this.initializeTimeout = null;
      if (clearReconnect) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (clearNoConnection) this.clearNoConnectionNotice();
      this.status = 'off';
      this.intentionallyClosed = true;
      if (
        this.socket &&
        (this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING)
      ) {
        this.socket.close(1000, reason);
      }
    },

    reconnect: function () {
      this.suspended = false;
      this.reconnectAttempt = 0;
      this.stop('reconnect');
      return this.init(this.store);
    },

    scheduleReconnect: function () {
      if (
        this.suspended ||
        this.reconnectTimer ||
        document.visibilityState === 'hidden'
      ) {
        return false;
      }

      const delayIndex = Math.min(
        this.reconnectAttempt,
        this.reconnectDelaysMs.length - 1
      );
      const delay = this.reconnectDelaysMs[delayIndex];
      this.reconnectAttempt += 1;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.init(this.store);
      }, delay);
      return true;
    },

    markConnected: function () {
      clearTimeout(this.initializeTimeout);
      clearTimeout(this.reconnectTimer);
      this.initializeTimeout = null;
      this.reconnectTimer = null;
      this.reconnectAttempt = 0;
      this.status = 'on';
      this.clearNoConnectionNotice();
    },

    scheduleNoConnectionNotice: function () {
      if (this.suspended || this.noConnectionTimer) return;
      this.noConnectionTimer = setTimeout(() => {
        this.noConnectionTimer = null;
        if (!this.suspended && this.status === 'off') {
          console.log('[wsManager] connection unavailable, showing no connection dialog');
          this.store.hooks.setNoConnection(true);
        }
      }, this.noConnectionGraceMs);
    },

    clearNoConnectionNotice: function () {
      clearTimeout(this.noConnectionTimer);
      this.noConnectionTimer = null;
      this.store?.hooks?.setNoConnection(false);
    },

    suspend: function (reason = 'suspended') {
      this.suspended = true;
      this.stop(reason);
    },

    resume: function ({ forceIfStale = false } = {}) {
      this.suspended = false;
      const socketIsActive =
        this.socket &&
        (this.socket.readyState === WebSocket.OPEN ||
          this.socket.readyState === WebSocket.CONNECTING);
      const socketIsStale =
        forceIfStale &&
        (this.lastMessageAt === 0 || Date.now() - this.lastMessageAt >=
          (this.socket?.readyState === WebSocket.CONNECTING
            ? this.connectionTimeoutMs : this.watchdogTimeoutMs));

      if (socketIsActive && !socketIsStale) {
        return false;
      }

      this.reconnectAttempt = 0;
      this.stop(socketIsStale ? 'stale-resume' : 'resume');
      return this.init(this.store);
    },

    fail: function () {
      this.scheduleNoConnectionNotice();
      this.stop('failure', false, false);
      this.scheduleReconnect();
    }
  };
}
